import axios from "axios";
import {
  BuildingAutocompleteResult,
  CityAutocompleteResult,
  EnergyClass,
  GeocodedAddress,
  LandPlotNumber,
  PenCertificate,
  Renovation,
  StreetAutocompleteResult,
} from "../types/geocoding.types";
import { PropertyPolygon, ValuationZoneClosestCity } from "../types/api.types";
import { PropertyFloodRisk } from "../types/appraiser.types";

const geocodeInstance = axios.create({
  baseURL: "https://geocoding.valuations.lt/dedicated-valuations",
});

export const getCities = async (query: string) => {
  const { data } = await geocodeInstance.get<CityAutocompleteResult[]>(
    `/autocomplete/residential_areas`,
    {
      params: {
        city: query,
      },
    }
  );
  return data;
};

export const getStreets = async (query: string, cityId: string) => {
  const { data } = await geocodeInstance.get<StreetAutocompleteResult[]>(
    `/autocomplete/streets`,
    {
      params: {
        street: query,
        city_id: cityId,
      },
    }
  );
  return data;
};

export const getBuildingNumbers = async (
  query: string,
  streetId: string,
  cityId: string
) => {
  const { data } = await geocodeInstance.get<BuildingAutocompleteResult[]>(
    `/autocomplete/building_numbers`,
    {
      params: {
        building_num: query,
        street_id: streetId,
        city_id: cityId,
      },
    }
  );
  return data;
};

export const getGeocodedAddress = async (lat: number, lng: number) => {
  const { data } = await geocodeInstance.get<GeocodedAddress | null>(
    `/geocode/reverse`,
    {
      params: {
        lat,
        lng,
      },
    }
  );
  return data;
};

export const getEnergyClass = async (
  city_id: string,
  street_id: string,
  building_nr: string
) => {
  const { data } = await geocodeInstance.get<EnergyClass | null>(
    `/energy_classes/by_address`,
    {
      params: {
        city_id,
        street_id,
        building_nr,
      },
    }
  );
  return data;
};

export const getPenCertificate = async (uniqueNr: string) => {
  try {
    const { data } = await geocodeInstance.get<PenCertificate | null>(
      `/energy_classes/pen_certificate`,
      {
        params: {
          unique_nr: uniqueNr,
        },
      }
    );
    return data;
  } catch {
    return null;
  }
};

export const getRenovation = async (lat: number, lng: number) => {
  const { data } = await geocodeInstance.get<Renovation | null>(
    `/energy_classes/renovation`,
    {
      params: {
        lat,
        lng,
      },
    }
  );
  return data;
};

export const getPropertyPlot = async (lat: number, lng: number) => {
  const { data } = await geocodeInstance.get<PropertyPolygon>(
    `/plot/location_plot`,
    {
      params: {
        lat,
        lng,
      },
    }
  );

  return data;
};

export const getValuationZoneClosestCityInfo = async (
  lat: number,
  lng: number
) => {
  const { data } = await geocodeInstance.get<ValuationZoneClosestCity>(
    `/valuation_zone/closest_city`,
    {
      params: {
        lat,
        lng,
      },
    }
  );

  return data;
};

export const getPropertyFloodRisk = async (lat: number, lng: number) => {
  const { data } = await geocodeInstance.get<PropertyFloodRisk>(
    `/flood/is_property_in_flood_zone`,
    {
      params: {
        lat,
        lng,
      },
    }
  );
  return data;
};

export const getLandPlotNumberInfo = async (
  city_id: string,
  street_id: string,
  building_nr: string
) => {
  const { data } = await geocodeInstance.get<LandPlotNumber | null>(
    "/plot/land_plot_number/by_address",
    {
      params: {
        city_id,
        street_id,
        building_nr,
      },
    }
  );
  return data;
};
